import { Transform } from "../../../../geometry/transformation";
import { sharedPointMapperRef, sharedEditorCanvas, sharedRubberCanvas, DrawOptions, sharedEditedHotspot } from "../../interface/pointMappings";
import { recalculateShape } from "../../util/shapeEdit";
//todo: check and remove if not needed...
import { hitStrokeCorrection, strokeCorrection } from "../../util/strokeCorrection";
import { RubberDataBase, registerInteractionHandlers, ShapeInteractionHandlers } from "../interactionManager";
import { isSamePosition } from "../../../../geometry/geometry";
import * as Global from "../../../../global"
import { hitManager } from "../hitManager"
//import { create } from "lodash";

export interface LineRubberData extends RubberDataBase {
    points: Point2d[],
    hotspot?: Hotspot2d
}

declare module "../interactionManager" {
  interface ShapeActionHandlersMap {
    line: ToolEntries & {
      create: {
        rubberData: LineRubberData;
        settings: LineShape;
      };
      edit: {
        rubberData: LineRubberData;
        settings: LineShape;
      };
    };
  }
}

export class Line {

    // draw functions
    static render(line: LineShape, isSelected: boolean, lineTypes: LineType[]) {
      const t = new Transform(line.transformation);

      const points = line.points.map(point => t.point2d(point));
      const drawOptions:DrawOptions = {
        color: line.stroke,
        width: line.strokeWidth,
        lineStyle: lineTypes.find(o => o.id === line.dashID)?.dash ?? [],
      }
      sharedEditorCanvas.current.drawPolyLine(points, drawOptions);

      const hotspots: Hotspot2d[] = isSelected ? points.map((p, index) => { return { x: p.x, y: p.y, type: line.type, shapeId: line.id, innerId: index } }) : [];

      return hotspots;
    }

    static isHit(line: LineShape, p:Point2d) {
        for (let i = 1; i < line.points.length; ++i) {
            const tr = new Transform(line.transformation);
            const p1 = tr.point2d({ x: line.points[i - 1].x, y: line.points[i - 1].y});
            const x1 = p1.x;
            const y1 = p1.y;
            const p2 = tr.point2d({ x: line.points[i].x, y: line.points[i].y});
            const x2 = p2.x;
            const y2 = p2.y;
            const width = 15 / sharedPointMapperRef.current.zoom;

            const dx = x2 - x1;
            const dy = y2 - y1;
            const lenSq = dx * dx + dy * dy;

            const t = ((p.x - x1) * dx + (p.y - y1) * dy) / lenSq;
            if (t < 0 || t > 1) continue;

            const px = x1 + t * dx;
            const py = y1 + t * dy;
            const dist = Math.hypot(px - p.x, py - p.y);
            if (dist <= width / 2) {
                return true;
            }
        }
        return false;
    }

    //--------------------------------------------------------------------- create
    static assembleShapeToInsert<L>(shape: L): L {
        return shape;
    }

}

const LineCreateHandlers: ShapeInteractionHandlers<LineRubberData, LineShape> = {
    rubberInputInit: (): LineRubberData => {
        const newRubberData: LineRubberData = {
            points: [],
            hotspot: undefined,
            finished: false,
        }

        return newRubberData;
    },
    rubberInputRenderer: (rubberPointData: LineRubberData, cursorPosition: Point2d) => {
        const dotDrawOptions: DrawOptions = {
            width: Global.DEFAULT_RUBBERLINE_DOT_PERIMETER_WIDTH,
            color: Global.DEFAULT_RUBBERLINE_DOT_PERIMETER_COLOR,
            fillColor: Global.DEFAULT_RUBBERLINE_DOT_FILL_COLOR,
        }

        rubberPointData.points.forEach(p => {
            sharedRubberCanvas.current.drawCircle(p, 5, dotDrawOptions);
        });

        const lineDrawOptions: DrawOptions = {
            width: Global.DEFAULT_RUBBERLINE_WIDTH,
            color: Global.DEFAULT_RUBBERLINE_COLOR,
        }
        if (rubberPointData.points.length > 0) {
            const points = [...rubberPointData.points, cursorPosition];
            sharedRubberCanvas.current.drawPolyLine(points, lineDrawOptions);
        }
    },
    rubberInputUpdater: (rubberPointData: LineRubberData, cursorPosition: Point2d) : LineRubberData => {
        if (rubberPointData.points.length > 0) {
            if (isSamePosition(rubberPointData.points[rubberPointData.points.length - 1], cursorPosition)) {
                rubberPointData.finished = true;
                return rubberPointData;
            }
        }

        return {...rubberPointData, points: [...rubberPointData.points, cursorPosition]};
    },
    callback: (shape: LineShape, rubberPointData: LineRubberData) : GeometricShape => {
        //todo: clean this up..
        return recalculateShape(Line.assembleShapeToInsert<LineShape>({
            ...shape,
            id: 0,
            strokeWidth: strokeCorrection(shape.strokeWidth, sharedPointMapperRef.current.zoom),
            hitStrokeWidth: hitStrokeCorrection(shape.hitStrokeWidth, shape.hitStrokeWidth, sharedPointMapperRef.current.zoom),
            closed: false,
            points: rubberPointData.points
        }));
    },
}

const LineHotspotEditHandlers: ShapeInteractionHandlers<LineRubberData, LineShape> = {
    rubberInputInit: (shape: LineShape): LineRubberData => {
        //console.log("hottenspotten", sharedEditedHotspot.current)
        let rubberData: LineRubberData = {
            points: [],
            hotspot: sharedEditedHotspot.current,
            finished: false
        };
        shape.points.forEach(p => {
            rubberData.points.push(p);
        })

        return rubberData;
    },
    rubberInputRenderer: (rubberPointData: LineRubberData, cursorPosition: Point2d) => {
        const dotDrawOptions: DrawOptions = {
            width: Global.DEFAULT_RUBBERLINE_DOT_PERIMETER_WIDTH,
            color: Global.DEFAULT_RUBBERLINE_DOT_PERIMETER_COLOR,
            fillColor: Global.DEFAULT_RUBBERLINE_DOT_FILL_COLOR,
        }

        rubberPointData.points.forEach(p => {
            sharedRubberCanvas.current.drawCircle(p, 5, dotDrawOptions);
        });

        let lineDrawOptions: DrawOptions = {
            width: Global.DEFAULT_RUBBERLINE_WIDTH,
            color: Global.DEFAULT_RUBBERLINE_COLOR,
            lineStyle: Global.LINE_SOLID,
        }
        if (rubberPointData.points.length > 0) {
            //sharedRubberCanvas.current.drawPolyLine(rubberPointData.points, lineDrawOptions);
            if (!rubberPointData.hotspot) return;

            if (rubberPointData.hotspot.innerId > 0) {
                let points = [rubberPointData.points[rubberPointData.hotspot.innerId - 1], cursorPosition];
                sharedRubberCanvas.current.drawPolyLine(points, lineDrawOptions);
            }
            lineDrawOptions.lineStyle = Global.LINE_DOTTED;
            let points = [rubberPointData.points[rubberPointData.hotspot.innerId], cursorPosition];
            sharedRubberCanvas.current.drawPolyLine(points, lineDrawOptions);
            lineDrawOptions.lineStyle = Global.LINE_SOLID;
            if (rubberPointData.hotspot.innerId < rubberPointData.points.length - 1) {
                let points = [rubberPointData.points[rubberPointData.hotspot.innerId + 1], cursorPosition];
                sharedRubberCanvas.current.drawPolyLine(points, lineDrawOptions);
            }
        }
    },
    rubberInputUpdater: (rubberPointData: LineRubberData, cursorPosition: Point2d) : LineRubberData => {
        if (!rubberPointData.hotspot) return rubberPointData;

        console.log("updating position");
        rubberPointData.points[rubberPointData.hotspot?.innerId] = cursorPosition;
        rubberPointData.finished = true;

        return rubberPointData;
    },
    callback: (shape: LineShape, rubberPointData: LineRubberData) : GeometricShape => {
        const newShape = {
            ...shape,
            points: rubberPointData.points,
        }
        return newShape;
    },
}

hitManager.register("line", Line.isHit);
registerInteractionHandlers("line", "create", LineCreateHandlers);
registerInteractionHandlers("line", "edit", LineHotspotEditHandlers);
